import { useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  ChartBar,
  ChevronRight,
  LineChart as LineChartIcon,
  PackageCheck,
  Receipt,
  Sparkles,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { KPICard } from '@/components/charts/KPICard';
import { LineChart } from '@/components/charts/LineChart';
import {
  ChartModeToggle,
  type ChartGranularity,
  type SeasonalityMode,
} from '@/components/charts/ChartModeToggle';
import {
  bucketBySeasonality,
  disabledGranularitiesFor,
  granularityForSeasonality,
} from '@/components/charts/seasonality';
import { mergeBucketedPrevious, mergeIndexedPrevious } from '@/components/charts/compareSeries';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { WhyKpi } from '@/components/charts/WhyKpi';
import { SnapshotMenu } from '@/components/system/SnapshotMenu';
import { compareRangeFor, useFilters, rangeISO } from '@/state/filters';
import { useOverview, useOrdersTimeseries, useOverviewContributors } from '@/api/bi';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/AuthProvider';
import { useUserPrefs } from '@/api/bi/settings';
import { ACTIVE_ORDER_STATUSES, statusDot, statusLabel } from '@/lib/status';

/**
 * Executive overview — the page everyone lands on.
 *
 * Stage 0 polish:
 *   - Personalised welcome strip with brand-tinted dotgrid + a NEW sticker.
 *   - "What changed" chip rail summarises the biggest movers vs prev period
 *     so executives can spot anomalies in 2 seconds.
 *   - All KPIs animate when their numbers move (rAF, ease-out, tabular).
 *   - Compare-mode aware: deltas hide when the user picks "Off".
 *
 * No new backend endpoints — derived entirely from /api/bi/overview/.
 */
export function OverviewPage() {
  const { user } = useAuth();
  const range = useFilters((s) => s.range);
  const storeId = useFilters((s) => s.storeId);
  const compareMode = useFilters((s) => s.compareMode);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const compareEnabled = compareMode !== 'none';
  const compareRange = useMemo(
    () => compareRangeFor(range, compareMode, timeRangeMode),
    [range, compareMode, timeRangeMode],
  );

  const overview = useOverview({
    start: iso.start,
    end: iso.end,
    compareStart: compareRange?.start,
    compareEnd: compareRange?.end,
    storeId,
  });

  // Granularity + Seasonality are user-controlled (URL-backed) and seeded
  // by the existing auto-pick so cold-loads still feel right. We no longer
  // disable any granularity for wide ranges — the backend caps the window
  // at 400 days so the volume is bounded, and disabling `hour` broke the
  // Hour-of-day seasonality view (which needs hour-bucketed data).
  const autoGranularity = pickGranularity(range.from, range.to);
  const [granularity, setGranularity] = useState<ChartGranularity>(autoGranularity);
  const [seasonality, setSeasonality] = useState<SeasonalityMode>('none');
  const disabledGranularities = useMemo(
    () => disabledGranularitiesFor(range.from, range.to),
    [range.from, range.to],
  );
  // When seasonality is on, the client roll-up needs a specific server
  // granularity to make sense (hour-of-day → hour buckets, day-of-week →
  // day buckets). Force-snap so the chart isn't silently empty.
  const requiredGranularity = granularityForSeasonality(seasonality);
  const effectiveGranularity: ChartGranularity = requiredGranularity ?? granularity;

  const series = useOrdersTimeseries({
    start: iso.start,
    end: iso.end,
    storeId,
    granularity: effectiveGranularity,
  });
  const previousSeries = useOrdersTimeseries({
    start: compareRange?.start ?? '',
    end: compareRange?.end ?? '',
    storeId,
    granularity: effectiveGranularity,
    enabled: compareEnabled && Boolean(compareRange),
  });

  const chartData = useMemo(
    () =>
      mergeIndexedPrevious(
        series.data?.buckets ?? [],
        previousSeries.data?.buckets,
        ['gmv', 'orders'],
        compareEnabled,
      ),
    [series.data, previousSeries.data, compareEnabled],
  );
  const seasonalityChartData = useMemo(() => {
    if (seasonality === 'none') return [];
    const current = bucketBySeasonality(series.data?.buckets ?? [], {
      timeKey: 't',
      metrics: ['gmv', 'orders'],
      mode: seasonality,
      aggregate: 'sum',
    });
    const previous = bucketBySeasonality(previousSeries.data?.buckets ?? [], {
      timeKey: 't',
      metrics: ['gmv', 'orders'],
      mode: seasonality,
      aggregate: 'sum',
    });
    return mergeBucketedPrevious(current, previous, ['gmv', 'orders'], compareEnabled);
  }, [series.data, previousSeries.data, seasonality, compareEnabled]);

  const trend = useMemo(
    () => (series.data?.buckets ?? []).map((b) => ({ value: b.gmv })),
    [series.data],
  );
  const ordersTrend = useMemo(
    () => (series.data?.buckets ?? []).map((b) => ({ value: b.orders })),
    [series.data],
  );

  const kpis = overview.data?.current;
  const deltas = overview.data?.deltas;

  const movers = useMemo(() => deriveMovers(overview.data), [overview.data]);
  const anomalies = useMemo(() => deriveAnomalies(overview.data), [overview.data]);
  const contributors = useOverviewContributors({
    start: iso.start,
    end: iso.end,
    compareStart: compareRange?.start,
    compareEnd: compareRange?.end,
    storeId,
    metric: 'gmv',
    limit: 5,
  });
  const pareto = contributors.data?.totals;
  // Subscribed rather than read once, so toggling any of these in Settings
  // takes effect here immediately instead of on the next remount.
  const prefs = useUserPrefs();
  const showWelcome = prefs.show_welcome;
  const whyEnabled = prefs.why_popovers !== false;
  const nprFmt = (n: number) => fmt.npr(n, prefs.kpi_format === 'exact');

  return (
    <>
      {showWelcome ? (
        <WelcomeCard
          firstName={firstName(user?.name) ?? user?.username ?? null}
          compareModeLabel={compareLabel(compareMode)}
        />
      ) : null}

      <div className={showWelcome ? 'mt-6' : ''}>
        <PageHeader
          eyebrow="Executive"
          title="Today, at a glance"
          description="GMV, orders, AOV, fulfilment health and exceptions — narrowed to your selected period and store."
          actions={<SnapshotMenu filename="overview" />}
        />
      </div>

      {compareEnabled && movers.length > 0 ? (
        <ChangedChips chips={movers} anomalies={anomalies} pareto={pareto} />
      ) : null}

      {overview.isError ? (
        <ErrorState onRetry={() => void overview.refetch()} />
      ) : (
        <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            label="GMV (completed)"
            value={
              <AnimatedNumber
                value={kpis?.gmv ?? null}
                format={nprFmt}
                duration={550}
              />
            }
            deltaPct={compareEnabled ? deltas?.gmv_pct ?? null : null}
            trend={trend}
            loading={overview.isLoading}
            hint="Sum of final_total for completed orders in range."
            action={whyEnabled ? <WhyKpi metric="gmv" format={nprFmt} /> : null}
          />
          <KPICard
            label="Orders placed"
            value={
              <AnimatedNumber
                value={kpis?.orders_total ?? null}
                format={(n) => fmt.compact(n)}
                duration={500}
              />
            }
            deltaPct={compareEnabled ? deltas?.orders_total_pct ?? null : null}
            trend={ordersTrend}
            loading={overview.isLoading}
            hint="Orders created in range, all statuses."
            action={whyEnabled ? <WhyKpi metric="orders_total" /> : null}
          />
          <KPICard
            label="AOV (completed)"
            value={
              <AnimatedNumber
                value={kpis?.aov ?? null}
                format={(n) => fmt.npr(n)}
                duration={500}
              />
            }
            deltaPct={compareEnabled ? deltas?.aov_pct ?? null : null}
            loading={overview.isLoading}
            hint="Average final_total across completed orders."
          />
          <KPICard
            label="Cancel rate"
            value={
              <AnimatedNumber
                value={kpis?.cancel_rate ?? null}
                format={(n) => fmt.pct(n)}
                duration={500}
              />
            }
            deltaPct={compareEnabled ? deltas?.cancel_rate_pct ?? null : null}
            goodWhenUp={false}
            loading={overview.isLoading}
            hint="Cancelled / total in range. Lower is better."
            action={whyEnabled ? <WhyKpi metric="orders_cancelled" goodWhenUp={false} /> : null}
          />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* Trend chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>GMV & orders</CardTitle>
            <ChartModeToggle
              granularity={granularity}
              onGranularityChange={setGranularity}
              disabledGranularities={disabledGranularities}
              seasonality={seasonality}
              onSeasonalityChange={setSeasonality}
            />
          </CardHeader>
          <CardBody>
            {series.isLoading || (compareEnabled && previousSeries.isLoading) ? (
              <Skeleton className="h-[280px] w-full" />
            ) : series.isError ? (
              <ErrorState onRetry={() => void series.refetch()} />
            ) : chartData.length === 0 ? (
              // An axis-only chart reads as "GMV was flat at zero" rather than
              // "nothing was recorded in this window", which is the difference
              // between a bad day and a broken pipeline.
              <EmptyState
                icon={<LineChartIcon className="h-5 w-5" />}
                title="No orders in this period"
                description="Nothing was recorded for the selected date range and store. Widen the range or clear the store filter."
                className="h-[280px]"
              />
            ) : seasonality !== 'none' ? (
              <LineChart
                data={seasonalityChartData}
                xKey="bucket"
                series={[
                  { key: 'gmv', label: 'GMV (NPR)' },
                  { key: 'orders', label: 'Orders' },
                  ...(compareEnabled
                    ? [
                        {
                          key: 'gmv_prev',
                          label: 'GMV prev',
                          color: 'hsl(var(--primary) / 0.55)',
                          strokeDasharray: '4 4',
                        },
                        {
                          key: 'orders_prev',
                          label: 'Orders prev',
                          color: 'hsl(var(--info) / 0.55)',
                          strokeDasharray: '4 4',
                        },
                      ]
                    : []),
                ]}
                formatX={(s) => s}
                formatValue={(v) => fmt.compact(v)}
              />
            ) : (
              <LineChart
                data={chartData}
                xKey="t"
                series={[
                  { key: 'gmv', label: 'GMV (NPR)' },
                  { key: 'orders', label: 'Orders' },
                  ...(compareEnabled
                    ? [
                        {
                          key: 'gmv_prev',
                          label: 'GMV prev',
                          color: 'hsl(var(--primary) / 0.55)',
                          strokeDasharray: '4 4',
                        },
                        {
                          key: 'orders_prev',
                          label: 'Orders prev',
                          color: 'hsl(var(--info) / 0.55)',
                          strokeDasharray: '4 4',
                        },
                      ]
                    : []),
                ]}
                formatX={(s) => fmt.date(s, effectiveGranularity === 'hour' ? 'HH:mm' : 'd MMM')}
                formatValue={(v) => fmt.compact(v)}
              />
            )}
          </CardBody>
        </Card>

        {/* In-flight panel */}
        <Card>
          <CardHeader>
            <CardTitle>In-flight orders</CardTitle>
            <Badge tone="primary" className="gap-1.5">
              <span className="live-dot live-dot-primary" aria-hidden />
              live
            </Badge>
          </CardHeader>
          <CardBody>
            {overview.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <div className="space-y-3">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="font-display text-3xl font-bold tracking-tight kpi-num">
                      <AnimatedNumber value={overview.data?.in_flight.total ?? 0} duration={500} />
                    </p>
                    <p className="text-xs text-muted-foreground">orders being worked on right now</p>
                  </div>
                  <ExceptionsChip count={overview.data?.exceptions ?? 0} />
                </div>

                <ul className="space-y-1.5">
                  {ACTIVE_ORDER_STATUSES.map((s) => (
                    <li key={s} className="flex items-center justify-between text-xs">
                      <span className="inline-flex items-center gap-2 text-muted-foreground">
                        <span
                          className={cn('inline-block h-1.5 w-1.5 rounded-full', statusDot(s))}
                          aria-hidden
                        />
                        {statusLabel(s)}
                      </span>
                      <span className="kpi-num font-medium">
                        <AnimatedNumber
                          value={overview.data?.in_flight.by_status[s] ?? 0}
                          duration={350}
                        />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Secondary KPIs row */}
      <div className="mt-4 grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-4">
        <SmallStat
          icon={<Users className="h-4 w-4" />}
          label="Active users"
          value={overview.data?.active_users ?? null}
          format={(n) => fmt.compact(n)}
          loading={overview.isLoading}
        />
        <SmallStat
          icon={<PackageCheck className="h-4 w-4" />}
          label="Completed orders"
          value={kpis?.orders_completed ?? null}
          format={(n) => fmt.number(n)}
          deltaPct={compareEnabled ? deltas?.orders_completed_pct ?? null : null}
          loading={overview.isLoading}
        />
        <SmallStat
          icon={<Banknote className="h-4 w-4" />}
          label="COD share"
          value={kpis?.cod_share ?? null}
          format={(n) => fmt.pct(n)}
          deltaPct={compareEnabled ? deltas?.cod_share_pct ?? null : null}
          goodWhenUp={false}
          loading={overview.isLoading}
        />
        <SmallStat
          icon={<Receipt className="h-4 w-4" />}
          label="Cancelled orders"
          value={kpis?.orders_cancelled ?? null}
          format={(n) => fmt.number(n)}
          deltaPct={compareEnabled ? deltas?.cancel_rate_pct ?? null : null}
          goodWhenUp={false}
          loading={overview.isLoading}
        />
      </div>

      {/* Discreet helper hints */}
      <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <ChartBar className="h-3.5 w-3.5" aria-hidden />
        <span>
          Range: {fmt.date(range.from)} → {fmt.date(range.to)}
        </span>
        <span aria-hidden>·</span>
        <span>Auto-refreshes every minute.</span>
        <TrendingUp className="ml-auto h-3.5 w-3.5" aria-hidden />
      </div>
    </>
  );
}

/* ---------- Welcome card ---------- */

function WelcomeCard({
  firstName,
  compareModeLabel,
}: {
  firstName: string | null;
  compareModeLabel: string;
}) {
  const greeting = useMemo(() => greetingFor(new Date()), []);

  return (
    <Card className="bg-dotgrid-brand relative overflow-hidden">
      <CardBody className="!pt-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary ring-1 ring-inset ring-primary/30">
                <Sparkles className="h-3 w-3" aria-hidden />
                Fasto BI
              </span>
              <span
                className="sticker-rotate-r inline-flex items-center rounded-sm bg-warning px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-warning-foreground shadow-[0_1px_0_rgba(0,0,0,0.15)]"
                aria-label="New"
              >
                fresh!
              </span>
            </div>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
              {greeting}{firstName ? `, ${firstName}` : ''} — let's see what moved.
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Numbers below are auto-comparing to your selected window: <span className="font-medium text-foreground">{compareModeLabel}</span>. Hit{' '}
              <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono text-[10px]">⌘K</kbd> to jump
              anywhere.
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function greetingFor(d: Date): string {
  const h = d.getHours();
  if (h < 5) return 'Burning the midnight oil';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 21) return 'Good evening';
  return 'Up late';
}

function compareLabel(mode: 'previous-period' | 'previous-year' | 'none'): string {
  if (mode === 'previous-year') return 'previous year';
  if (mode === 'none') return 'no comparison';
  return 'previous period';
}

function firstName(name: string | null | undefined): string | null {
  if (!name) return null;
  return name.trim().split(/\s+/)[0] ?? null;
}

function pickGranularity(from: Date, to: Date): 'hour' | 'day' | 'week' {
  const days = Math.max(1, Math.round((to.getTime() - from.getTime()) / 86_400_000));
  if (days <= 2) return 'hour';
  if (days <= 60) return 'day';
  return 'week';
}

/* ---------- "What changed" chip rail ---------- */

interface MoverChip {
  label: string;
  pct: number;
  goodWhenUp: boolean;
  to?: string;
}

interface AnomalyChip {
  label: string;
  pct: number;
  kind: 'spike' | 'dip';
  reason: string;
}

const ANOMALY_THRESHOLD = 0.30; // 30% delta is considered "noteworthy".

function deriveAnomalies(overview: ReturnType<typeof useOverview>['data']): AnomalyChip[] {
  if (!overview) return [];
  const checks: { label: string; pct: number; goodWhenUp: boolean }[] = [
    { label: 'GMV', pct: overview.deltas.gmv_pct ?? 0, goodWhenUp: true },
    { label: 'Orders', pct: overview.deltas.orders_total_pct ?? 0, goodWhenUp: true },
    { label: 'Cancel', pct: overview.deltas.cancel_rate_pct ?? 0, goodWhenUp: false },
    { label: 'AOV', pct: overview.deltas.aov_pct ?? 0, goodWhenUp: true },
  ];
  const out: AnomalyChip[] = [];
  for (const c of checks) {
    if (Math.abs(c.pct) < ANOMALY_THRESHOLD) continue;
    const positive = c.pct > 0;
    const isBad = c.goodWhenUp ? !positive : positive;
    if (!isBad) continue; // only surface genuinely concerning swings.
    out.push({
      label: c.label,
      pct: c.pct,
      kind: positive ? 'spike' : 'dip',
      reason: `${c.label} moved ${fmt.delta(c.pct, { unit: 'pct' })} vs prev period (>|30%|).`,
    });
  }
  return out.slice(0, 2);
}

function deriveMovers(overview: ReturnType<typeof useOverview>['data']): MoverChip[] {
  if (!overview) return [];
  const candidates: MoverChip[] = [
    { label: 'GMV', pct: overview.deltas.gmv_pct ?? 0, goodWhenUp: true, to: '/orders' },
    { label: 'Orders', pct: overview.deltas.orders_total_pct ?? 0, goodWhenUp: true, to: '/orders' },
    { label: 'AOV', pct: overview.deltas.aov_pct ?? 0, goodWhenUp: true, to: '/orders' },
    { label: 'Cancel rate', pct: overview.deltas.cancel_rate_pct ?? 0, goodWhenUp: false, to: '/orders' },
    { label: 'Completed', pct: overview.deltas.orders_completed_pct ?? 0, goodWhenUp: true, to: '/orders' },
    { label: 'COD share', pct: overview.deltas.cod_share_pct ?? 0, goodWhenUp: false, to: '/orders' },
  ];
  return candidates
    .filter((c) => Math.abs(c.pct) >= 0.005)
    .sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct))
    .slice(0, 5);
}

function ChangedChips({
  chips,
  anomalies,
  pareto,
}: {
  chips: MoverChip[];
  anomalies: AnomalyChip[];
  pareto?: { pareto_80_count: number; pareto_80_pct: number; store_count: number };
}) {
  const navigate = useNavigate();
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        <Activity className="h-3 w-3" aria-hidden /> What changed
      </span>
      {chips.map((c) => {
        const positive = c.pct > 0;
        const isGood = c.goodWhenUp ? positive : !positive;
        const Icon = positive ? ArrowUpRight : ArrowDownRight;
        return (
          <button
            type="button"
            key={c.label}
            onClick={() => c.to && navigate(c.to)}
            className={cn(
              'press group inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset focus-ring',
              isGood
                ? 'border-success/30 bg-success/10 text-success ring-success/20'
                : 'border-danger/30 bg-danger/10 text-danger ring-danger/20',
            )}
          >
            <Icon className="h-3 w-3" aria-hidden />
            <span>{c.label}</span>
            <span className="kpi-num">{fmt.delta(c.pct, { unit: 'pct' })}</span>
            <ChevronRight className="h-3 w-3 opacity-50 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden />
          </button>
        );
      })}
      {anomalies.map((a) => (
        <span
          key={`anom-${a.label}`}
          className="press inline-flex items-center gap-1.5 rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning-soft ring-1 ring-inset ring-warning/30"
          title={a.reason}
        >
          <AlertTriangle className="h-3 w-3" aria-hidden />
          {a.label} {a.kind === 'spike' ? 'spike' : 'dip'}
          <span className="kpi-num">{fmt.delta(a.pct, { unit: 'pct' })}</span>
        </span>
      ))}
      {pareto && pareto.store_count > 1 && pareto.pareto_80_pct > 0 && pareto.pareto_80_pct <= 0.34 ? (
        <span
          className="inline-flex items-center gap-1.5 rounded-full border border-info/30 bg-info/10 px-2 py-0.5 text-[11px] font-medium text-info ring-1 ring-inset ring-info/20"
          title={`${pareto.pareto_80_count} of ${pareto.store_count} stores drive 80% of GMV`}
        >
          <ChartBar className="h-3 w-3" aria-hidden />
          {pareto.pareto_80_count}/{pareto.store_count} stores ≈ 80%
        </span>
      ) : null}
    </div>
  );
}

function ExceptionsChip({ count }: { count: number }) {
  if (count === 0) {
    return (
      <Badge tone="success">
        <ArrowDownRight className="h-3 w-3" aria-hidden />
        no exceptions
      </Badge>
    );
  }
  return (
    <Badge tone="warning">
      <AlertTriangle className="h-3 w-3" aria-hidden />
      {fmt.number(count)} exception{count === 1 ? '' : 's'}
    </Badge>
  );
}

/* ---------- Small stat tile (with animation) ---------- */

interface SmallStatProps {
  icon: React.ReactNode;
  label: string;
  value: number | null;
  format: (n: number) => string;
  deltaPct?: number | null;
  goodWhenUp?: boolean;
  loading?: boolean;
}

function SmallStat({
  icon,
  label,
  value,
  format,
  deltaPct,
  goodWhenUp = true,
  loading,
}: SmallStatProps) {
  return (
    <Card>
      <CardBody className="!pt-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="text-muted-foreground/80">{icon}</span>
          <span>{label}</span>
        </div>
        {loading ? (
          <Skeleton className="mt-2 h-7 w-24" />
        ) : (
          <p className="mt-2 font-display text-xl font-bold kpi-num">
            <AnimatedNumber value={value} format={format} duration={500} />
          </p>
        )}
        {deltaPct != null ? (
          <p
            className={cn(
              'mt-1 inline-flex items-center gap-0.5 text-[11px] font-medium',
              deltaTone(deltaPct, goodWhenUp),
            )}
          >
            {deltaPct > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {fmt.delta(deltaPct, { unit: 'pct' })} vs prev
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}

function deltaTone(deltaPct: number, goodWhenUp: boolean): string {
  const positive = deltaPct > 0;
  const isGood = goodWhenUp ? positive : !positive;
  if (deltaPct === 0) return 'text-muted-foreground';
  return isGood ? 'text-success' : 'text-danger';
}
